import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ArticleCard from "../components/ArticleCard";
import SmartImage from "../components/SmartImage";
import HotBadge from "../components/HotBadge";
import ArtistSearchHero from "../components/ArtistSearchHero";
import FeaturedReleases from "../components/FeaturedReleases";
import { getArticles, seedData } from "../lib/api";
import { useAudioPlayer, setRadioScope } from "../hooks/useAudioPlayer";
import axios from "axios";
import { Sparkles, TrendingUp, Zap, Play, Pause, ListMusic, Disc, Music, ChevronRight, Star, Radio } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [latestCDs, setLatestCDs] = useState([]);
  const [topWeek, setTopWeek] = useState({ top_cds: [], top_videos: [], top_articles: [] });
  // PROGRESSIVE LOADING: nunca bloqueia o paint da página com fullscreen.
  // Cada seção renderiza seu próprio skeleton enquanto carrega.
  const { playingId, handlePlay } = useAudioPlayer();

  useEffect(() => {
    let cancelled = false;

    // Seed data fire-and-forget
    seedData().catch(() => {});

    // === Stage 1: libera o paint do hero IMEDIATAMENTE ===
    // FeaturedReleases tem skeleton próprio e carrega CDs por conta dele.
    // O resto (articles, top-week, playlists, ranking) carrega em paralelo
    // mas SEM bloquear o paint da página inteira.

    // === Stage 2: carrega cada seção em paralelo (cada uma renderiza assim que chega) ===
    getArticles({ status: "published", limit: 50 })
      .then((r) => !cancelled && setArticles(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    axios.get(`${API}/articles/featured`)
      .then((r) => !cancelled && setFeatured(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    axios.get(`${API}/stats/top-week`)
      .then((r) => !cancelled && setTopWeek(r.data || { top_cds: [], top_videos: [], top_articles: [] }))
      .catch(() => {});

    axios.get(`${API}/musics/ranking?limit=5`)
      .then((r) => !cancelled && setRanking(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    axios.get(`${API}/playlists`)
      .then((r) => !cancelled && setPlaylists(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    axios.get(`${API}/cd-submissions/public?limit=12&include_tracks=1`)
      .then((r) => {
        if (cancelled) return;
        const cds = Array.isArray(r.data) ? r.data : [];
        setLatestCDs(cds.slice(0, 12));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const formatPlays = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num;
  };

  const [startingRadio, setStartingRadio] = useState(false);

  // Top 5 CDs por plays para mostrar selo "MAIS TOCADAS" nos cards
  const hotCdRanks = useMemo(() => {
    const sorted = [...latestCDs].filter(c => (c.plays || 0) > 0).sort((a, b) => (b.plays || 0) - (a.plays || 0));
    const map = {};
    sorted.slice(0, 5).forEach((cd, i) => { map[cd.id] = i + 1; });
    return map;
  }, [latestCDs]);
  const startRadio = async () => {
    setStartingRadio(true);
    try {
      // Lista LEVE (sem tracks) — só para escolher aleatório rapidinho
      const { data } = await axios.get(`${API}/cd-submissions/public?limit=200`);
      if (!Array.isArray(data) || data.length === 0) {
        toast.error("Nenhum CD disponível na Vitrine ainda.");
        return;
      }
      const picked = data[Math.floor(Math.random() * data.length)];
      // Detalhe do CD escolhido (com tracks)
      const { data: cd } = await axios.get(`${API}/cd-submissions/${picked.id}`);
      const tracks = [...(cd.tracks || [])].sort(
        (a, b) => (a.track_number || 0) - (b.track_number || 0)
      );
      if (tracks.length === 0) {
        toast.error("CD sorteado sem faixas. Tente novamente.");
        return;
      }
      const queue = tracks.map((t, idx) => {
        const src = t.preview_url || t.audio_url || "";
        return {
          id: `cd-${cd.id}-${idx}`,
          title: t.title,
          artist_name: cd.artist_name,
          artist_image: cd.cover_url ? `${process.env.REACT_APP_BACKEND_URL}${cd.cover_url}` : null,
          preview_url: src && !src.startsWith("http") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src,
          _source_cd_id: cd.id,
        };
      }).filter((t) => t.preview_url);
      if (queue.length === 0) {
        toast.error("CD sem faixas reproduzíveis.");
        return;
      }
      // 📻 Rádio da HOME = escopo geral (mistura todos os artistas)
      setRadioScope({ type: "all" });
      handlePlay(queue[0], queue);
      toast.success(`📻 Rádio ligada — ${cd.artist_name}`, { duration: 3000 });
    } catch (e) {
      toast.error("Erro ao iniciar a rádio. Tente novamente.");
    } finally {
      setStartingRadio(false);
    }
  };

  const playCD = (cd) => {
    const tracks = [...(cd.tracks || [])].sort(
      (a, b) => (a.track_number || 0) - (b.track_number || 0)
    );
    if (tracks.length === 0) return;
    const queue = tracks.map((t, idx) => {
      const src = t.preview_url || t.audio_url || "";
      return {
        id: `cd-${cd.id}-${idx}`,
        title: t.title,
        artist_name: cd.artist_name,
        artist_image: cd.cover_url ? `${process.env.REACT_APP_BACKEND_URL}${cd.cover_url}` : null,
        preview_url: src && !src.startsWith("http") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src,
        _source_cd_id: cd.id,
      };
    }).filter((t) => t.preview_url);
    if (queue.length === 0) return;
    // Clique em um card de CD na home → escopo do artista (toca o álbum inteiro,
    // depois vai pra outro CD do mesmo artista e por fim rádio geral).
    setRadioScope({ type: "artist", artistName: cd.artist_name });
    handlePlay(queue[0], queue);
  };

  const featuredArticle = articles[0];
  const secondaryArticles = articles.slice(1, 4);
  const restArticles = articles.slice(4, 12);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF007F]/20 via-transparent to-[#00E5FF]/10"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF007F]/30 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#00E5FF]/20 rounded-full blur-[120px]"></div>
        
        <div className="relative max-w-7xl mx-auto text-center py-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF007F]/20 border border-[#FF007F]/30 rounded-full text-[#FF007F] text-sm font-medium mb-6">
            <Zap size={16} />
            Portal #1 de Arrocha do Brasil
          </div>
          <h1 className="font-heading font-black tracking-tighter text-white mb-4 leading-none" translate="no">
            <span className="sr-only">arrochaplay</span>
            <img
              src="/arrochaplay-logo.png?v=11"
              alt="arrochaplay"
              translate="no"
              draggable="false"
              className="mx-auto h-24 sm:h-36 lg:h-48 w-auto select-none drop-shadow-[0_0_36px_rgba(255,0,127,0.45)]"
              data-testid="hero-logo"
            />
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
            Notícias, músicas, rankings e playlists do mundo do Arrocha. Tudo em um só lugar!
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <button
              onClick={startRadio}
              disabled={startingRadio}
              className="group flex items-center gap-2 px-7 py-4 bg-gradient-to-r from-[#FF007F] to-[#FF3399] hover:from-[#FF3399] hover:to-[#FF007F] text-white font-bold rounded-full transition-all hover:scale-105 shadow-lg shadow-[#FF007F]/40 ring-2 ring-[#FF007F]/30 hover:ring-[#FF007F]/60 disabled:opacity-60"
              data-testid="cta-radio"
              aria-label="Ligar a Rádio ArrochaPlay"
            >
              {startingRadio ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Radio size={22} className="group-hover:animate-pulse" />
              )}
              <span className="text-base sm:text-lg" translate="no">
                {startingRadio ? "Ligando..." : "📻 Rádio ArrochaPlay"}
              </span>
            </button>
            <Link
              to="/ranking"
              className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full transition-all border border-white/20"
              data-testid="cta-ranking"
            >
              <TrendingUp size={20} />
              Ver Top 10
            </Link>
            <Link
              to="/playlists"
              className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full transition-all border border-white/20"
              data-testid="cta-vitrine"
            >
              <Disc size={20} />
              CDs Completos
            </Link>
          </div>

          {/* Buscador de artista */}
          <ArtistSearchHero />
        </div>
      </section>

      {/* Lançamentos em Destaque (rotação automática 1 min) */}
      <FeaturedReleases />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ⭐ TOP DA SEMANA — agregação automática últimos 7 dias */}
        {(topWeek.top_cds.length + topWeek.top_videos.length + topWeek.top_articles.length) > 0 && (
          <section className="mb-12" data-testid="top-week-section">
            <div className="flex items-end justify-between mb-5 gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-[#FF007F] flex items-center justify-center">
                  <Star size={20} className="text-white" fill="white" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-black text-white">
                    Top da <span className="text-[#FF007F] italic">Semana</span>
                  </h2>
                  <p className="text-zinc-500 text-xs sm:text-sm">Os mais ouvidos, vistos e lidos nos últimos 7 dias</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* CDs */}
              <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-4" data-testid="top-week-cds">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                  <Disc size={18} className="text-[#FF007F]" /> CDs mais tocados
                </h3>
                {topWeek.top_cds.length === 0 ? (
                  <p className="text-zinc-500 text-sm">Sem dados no período.</p>
                ) : (
                  <ol className="space-y-2">
                    {topWeek.top_cds.slice(0, 5).map((cd, idx) => (
                      <li key={cd.id}>
                        <Link to={cd.url} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition group">
                          <span className="text-[#FF007F] font-black text-lg w-6 tabular-nums">{idx + 1}</span>
                          <div className="relative w-12 h-12 flex-shrink-0">
                            {idx < 5 && <HotBadge rank={idx + 1} compact position="tr" />}
                            {cd.cover_url ? (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${cd.cover_url}`} alt={cd.title} className="w-12 h-12 rounded-md object-cover" loading="lazy" />
                            ) : (
                              <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center"><Disc size={20} className="text-white/40" /></div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-semibold truncate group-hover:text-[#FF007F] transition">{cd.title}</p>
                            <p className="text-zinc-500 text-xs truncate">{cd.artist_name}</p>
                          </div>
                          <span className="text-[10px] font-bold text-[#FF007F] flex-shrink-0 tabular-nums">{Number(cd.plays || 0).toLocaleString("pt-BR")} plays</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Vídeos */}
              <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-4" data-testid="top-week-videos">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                  <Play size={18} className="text-[#FF007F]" /> Vídeos da Vitrine
                </h3>
                {topWeek.top_videos.length === 0 ? (
                  <p className="text-zinc-500 text-sm">Sem dados no período.</p>
                ) : (
                  <ol className="space-y-2">
                    {topWeek.top_videos.slice(0, 5).map((v, idx) => (
                      <li key={v.id}>
                        <Link to={v.url} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition group">
                          <span className="text-[#FF007F] font-black text-lg w-6 tabular-nums">{idx + 1}</span>
                          {v.cover_url ? (
                            <img src={v.cover_url.startsWith("http") ? v.cover_url : `${process.env.REACT_APP_BACKEND_URL}${v.cover_url}`} alt={v.title} className="w-12 h-12 rounded-md object-cover flex-shrink-0" loading="lazy" />
                          ) : (
                            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center flex-shrink-0"><Play size={18} className="text-white/40" /></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-semibold truncate group-hover:text-[#FF007F] transition">{v.title}</p>
                            <p className="text-zinc-500 text-xs truncate">{v.artist_name}</p>
                          </div>
                          <span className="text-[10px] font-bold text-[#FF007F] flex-shrink-0">{v.views} views</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Matérias */}
              <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-4" data-testid="top-week-articles">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                  <TrendingUp size={18} className="text-[#FF007F]" /> Matérias mais lidas
                </h3>
                {topWeek.top_articles.length === 0 ? (
                  <p className="text-zinc-500 text-sm">Sem dados no período.</p>
                ) : (
                  <ol className="space-y-2">
                    {topWeek.top_articles.slice(0, 5).map((a, idx) => (
                      <li key={a.id}>
                        <Link to={a.url} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition group">
                          <span className="text-[#FF007F] font-black text-lg w-6 tabular-nums">{idx + 1}</span>
                          {a.cover_url ? (
                            <img src={a.cover_url.startsWith("http") ? a.cover_url : `${process.env.REACT_APP_BACKEND_URL}${a.cover_url}`} alt={a.title} className="w-12 h-12 rounded-md object-cover flex-shrink-0" loading="lazy" />
                          ) : (
                            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center flex-shrink-0"><TrendingUp size={18} className="text-white/40" /></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-semibold truncate group-hover:text-[#FF007F] transition">{a.title}</p>
                            <p className="text-zinc-500 text-xs truncate">{a.category}</p>
                          </div>
                          <span className="text-[10px] font-bold text-[#FF007F] flex-shrink-0">{a.views} views</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </section>
        )}

        {/* 🔥 Novos CDs (carrossel horizontal) */}
        {latestCDs.length > 0 && (
          <section className="mb-12" data-testid="latest-cds-section">
            <div className="flex items-end justify-between mb-5 gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-[#FF007F] flex items-center justify-center text-xl">
                  🔥
                </div>
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-black text-white">
                    Novos <span className="text-[#FF007F] italic">CDs</span>
                  </h2>
                  <p className="text-zinc-500 text-xs sm:text-sm">Acabam de chegar na plataforma</p>
                </div>
              </div>
              <Link
                to="/playlists"
                className="text-[#FF007F] hover:text-[#FF3399] text-sm font-bold flex items-center gap-1 whitespace-nowrap"
                data-testid="latest-cds-see-all"
              >
                Ver tudo <ChevronRight size={16} />
              </Link>
            </div>
            <div
              className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-0 sm:px-0 scrollbar-thin scrollbar-thumb-white/10"
              style={{ scrollbarWidth: "thin" }}
              data-testid="latest-cds-carousel"
            >
              {latestCDs.map((cd) => (
                <div
                  key={cd.id}
                  className="group relative flex-shrink-0 w-40 sm:w-48 bg-[#0F0F0F] border border-white/5 hover:border-[#FF007F]/40 rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
                  data-testid={`latest-cd-${cd.id}`}
                >
                  <Link to={`/cd/${cd.id}`} className="block">
                    <div className="aspect-square overflow-hidden bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 relative">
                      {hotCdRanks[cd.id] ? <HotBadge rank={hotCdRanks[cd.id]} /> : null}
                      {cd.cover_url ? (
                        <img
                          src={`${API.replace(/\/api$/, "")}${cd.cover_url}`}
                          alt={cd.cd_title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Disc size={32} className="text-white/40" />
                        </div>
                      )}
                      <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur px-2 py-1 rounded-full text-[10px] flex items-center gap-1 text-white">
                        <Music size={10} /> {cd.tracks?.length || 0}
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-[10px] text-[#FF007F] uppercase tracking-wider truncate font-bold">
                        {cd.artist_name}
                      </p>
                      <h3 className="font-semibold text-white text-sm truncate mt-0.5">
                        {cd.cd_title}
                      </h3>
                    </div>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      playCD(cd);
                    }}
                    className="absolute right-3 bottom-14 w-10 h-10 rounded-full bg-[#FF007F] hover:bg-[#FF3399] text-white shadow-lg shadow-[#FF007F]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all"
                    title={`Tocar ${cd.cd_title}`}
                    data-testid={`latest-cd-play-${cd.id}`}
                    aria-label={`Tocar CD ${cd.cd_title} de ${cd.artist_name}`}
                  >
                    <Play size={16} fill="white" className="ml-0.5" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Featured Articles (Destaques) */}
        {featured.length > 0 && (
          <section className="mb-12" data-testid="featured-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-[#FF007F] flex items-center justify-center">
                <Star className="text-white" size={20} fill="currentColor" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-black text-white">
                Destaques
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.slice(0, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/artigo/${article.id}`}
                  className="group relative bg-[#0F0F0F] border border-amber-500/20 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:-translate-y-1"
                  data-testid={`featured-${article.id}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-zinc-900 to-zinc-800">
                    <SmartImage
                      src={article.image_url}
                      alt={article.title}
                      aspect="fill"
                      rounded=""
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none z-10" />
                    <div className="absolute top-3 left-3 z-20 px-2.5 py-1 bg-amber-500 text-black text-[10px] font-black rounded-full flex items-center gap-1">
                      <Star size={10} fill="currentColor" /> DESTAQUE
                    </div>
                    {article.category && (
                      <div className="absolute top-3 right-3 z-20 px-2.5 py-1 bg-black/60 backdrop-blur text-white text-[10px] font-bold rounded-full">
                        {article.category}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold text-white leading-tight mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    {article.excerpt && (
                      <p className="text-zinc-400 text-sm line-clamp-2">{article.excerpt}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Top Section: Ranking + Playlists */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {/* Top 5 Ranking */}
          <div className="lg:col-span-2 bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <TrendingUp className="text-[#FF007F]" size={20} />
                Top 5 da Semana
              </h2>
              <Link to="/ranking" className="text-[#FF007F] text-sm flex items-center gap-1 hover:underline">
                Ver tudo <ChevronRight size={16} />
              </Link>
            </div>
            <div className="divide-y divide-white/5">
              {ranking.map((music, index) => (
                <div
                  key={music.id}
                  className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors"
                  data-testid={`home-ranking-${index}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
                    index < 3 
                      ? 'bg-gradient-to-br from-[#FFB800] to-[#FF007F] text-white' 
                      : 'bg-white/10 text-zinc-400'
                  }`}>
                    {index + 1}
                  </div>
                  {music.artist_image ? (
                    <img
                      src={music.artist_image}
                      alt=""
                      className="w-12 h-12 rounded-xl object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                      <Disc className="w-5 h-5 text-zinc-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{music.title}</h3>
                    <p className="text-zinc-500 text-xs sm:text-sm truncate flex items-center gap-2">
                      <span className="truncate">{music.artist_name}</span>
                      <span className="sm:hidden flex items-center gap-0.5 tabular-nums shrink-0 text-[#FF007F]/70">
                        <span className="opacity-50">•</span>
                        <Play size={9} fill="currentColor" />
                        {formatPlays(music.plays)}
                      </span>
                    </p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-white font-medium tabular-nums">{formatPlays(music.plays)}</p>
                    <p className="text-zinc-500 text-xs">plays</p>
                  </div>
                  <button 
                    onClick={(e) => { e.preventDefault(); setRadioScope({ type: "all" }); handlePlay(music, ranking); }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      playingId === music.id ? 'bg-[#FF007F] animate-pulse' : 'bg-[#FF007F]/20 hover:bg-[#FF007F]'
                    } ${!music.preview_url ? 'opacity-30 cursor-not-allowed' : ''}`}
                    data-testid={`play-btn-${music.id}`}
                    disabled={!music.preview_url}
                  >
                    {playingId === music.id ? (
                      <Pause size={16} fill="white" className="text-white" />
                    ) : (
                      <Play size={16} fill="white" className="text-white ml-0.5" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Playlists */}
          <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <ListMusic className="text-[#00E5FF]" size={20} />
                Playlists
              </h2>
              <Link to="/playlists" className="text-[#00E5FF] text-sm flex items-center gap-1 hover:underline">
                Ver tudo <ChevronRight size={16} />
              </Link>
            </div>
            <div className="p-4 space-y-3">
              {playlists.slice(0, 4).map((playlist, index) => (
                <Link
                  key={playlist.id}
                  to={`/playlist/${playlist.id}`}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors group"
                  data-testid={`home-playlist-${index}`}
                >
                  {playlist.cover_image ? (
                    <img
                      src={playlist.cover_image}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center">
                      <Music className="w-6 h-6 text-white/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate group-hover:text-[#00E5FF] transition-colors">
                      {playlist.name}
                    </h3>
                    <p className="text-zinc-500 text-xs">{playlist.music_count} músicas</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured News Section */}
        {featuredArticle && (
          <section className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-[#FF007F]" />
              <h2 className="font-heading text-xl font-bold text-white">Notícias em Destaque</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7">
                <ArticleCard article={featuredArticle} featured />
              </div>
              <div className="lg:col-span-5 flex flex-col gap-4">
                {secondaryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest News */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-white">Últimas Notícias</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {restArticles.map((article, index) => (
              <div 
                key={article.id} 
                className="animate-fade-in-up opacity-0"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <ArticleCard article={article} />
              </div>
            ))}
          </div>
        </section>

        {/* Empty State */}
        {articles.length === 0 && (
          <div className="text-center py-20">
            <Sparkles className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-white mb-2">
              Nenhuma notícia encontrada
            </h3>
            <p className="text-zinc-500">
              Em breve teremos novidades fresquinhas do mundo do Arrocha!
            </p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
